import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Home, ArrowLeft, Mail } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { PublicLangToggle } from "../components/PublicLangToggle";

/**
 * 404 page — friendly, with clear next steps.
 * Used by App.js for the catch-all route.
 *
 * Tâche 1328 (Eric 2026-06-30) — Bilingue, et la bascule FR|EN est POSÉE
 * dessus : cet écran s'affiche aussi pour un visiteur non connecté, qui n'a
 * donc pas la barre du tableau de bord. Sans ce bouton, la version anglaise
 * de la page serait inatteignable pour la seule personne à qui elle sert.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tr } = useLang();

  return (
    <div
      data-testid="page-not-found"
      className="min-h-screen flex items-center justify-center bg-[#0a4d3a] text-white px-6 relative"
    >
      <div className="absolute top-4 right-4">
        <PublicLangToggle />
      </div>
      <div className="max-w-2xl w-full">
        <div className="text-[#c89b3c] text-[120px] font-serif leading-none mb-4 select-none">
          404
        </div>
        <h1 className="text-3xl font-serif mb-3">{tr("Page introuvable", "Page not found")}</h1>
        <p className="text-white/80 text-lg mb-2">
          {tr("La page ", "The page ")}
          <code className="bg-black/40 px-2 py-0.5 rounded text-sm text-white">{location.pathname}</code>{" "}
          {tr("n'existe pas ou a été déplacée.", "does not exist or has been moved.")}
        </p>
        <p className="text-white/60 mb-8">
          {tr(
            "Si vous avez suivi un lien depuis Knoria, c'est probablement un bug de notre côté — contactez-nous et nous corrigerons rapidement.",
            "If you followed a link from Knoria, this is most likely a bug on our side — get in touch and we will fix it quickly.",
          )}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="not-found-back-btn"
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-transparent border-white/30 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {tr("Retour", "Back")}
          </Button>
          <Button
            data-testid="not-found-home-btn"
            onClick={() => navigate("/dashboard")}
            className="bg-[#c89b3c] hover:bg-[#b8893c] text-black font-semibold"
          >
            <Home className="w-4 h-4 mr-2" />
            {tr("Tableau de bord", "Dashboard")}
          </Button>
          <a
            href="mailto:support@knoria.com?subject=Page%20introuvable"
            className="inline-flex"
          >
            <Button
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10"
            >
              <Mail className="w-4 h-4 mr-2" />
              {tr("Signaler", "Report")}
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
